import type { DocumentData, DocumentReference } from 'firebase/firestore'
import {
  doc,
  type Firestore,
  type FirestoreDataConverter,
  type PartialWithFieldValue,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { getDocWithError } from '../../firestore-mobx/fetch'
import type { FirestoreUserProfileScoreMappingDefaults } from './schema'
import { schema, writeSchema } from './schema'

export * from './schema'

/**
 * The score-mapping defaults live in a single, fixed-id document under the
 * `score_mapping_defaults` subcollection of the user's profile:
 * `/user_profile/<userId>/score_mapping_defaults/default`.
 */
export const SCORE_MAPPING_DEFAULTS_DOC_ID = 'default'

const converter: FirestoreDataConverter<FirestoreUserProfileScoreMappingDefaults> =
  {
    toFirestore: (
      value: PartialWithFieldValue<FirestoreUserProfileScoreMappingDefaults>
    ) => {
      writeSchema.partial().parse(value)
      return value
    },
    fromFirestore: (snapshot: QueryDocumentSnapshot) => {
      const data = snapshot.data({ serverTimestamps: 'estimate' })
      return schema.parse(data)
    },
  }

export const getDocRef = (
  firestore: Firestore,
  userId: string
): DocumentReference<
  FirestoreUserProfileScoreMappingDefaults,
  DocumentData
> => {
  return doc(
    firestore,
    'user_profile',
    userId,
    'score_mapping_defaults',
    SCORE_MAPPING_DEFAULTS_DOC_ID
  ).withConverter(converter)
}

/**
 * Reads the user's saved score-mapping defaults. Returns null when none have
 * been saved yet (the document does not exist).
 */
export const fetchUserProfileScoreMappingDefaults = async (
  repository: FirebaseRepository,
  params: { userId: string }
): Promise<FirestoreUserProfileScoreMappingDefaults | null> => {
  const ref = getDocRef(repository.firestore, params.userId)
  const snapshot = await getDocWithError(
    ref,
    'FetchUserProfileScoreMappingDefaultsError'
  )
  if (!snapshot.exists()) return null
  return snapshot.data()
}
